import axiosInstance from './axiosInstance';
import type { Response } from './axiosInstance';

export type ShareUsersParam = {
  search_term: string;
  page: number;
  page_size: number;
}

export type ShareUser = {
  username: string;
  email: string;
  lastName: string;
  firstName: string;
};

export type ShareUsersData = {
  users: ShareUser[];
  page: number;
  page_size: number;
  has_more: boolean;
}

export type ShareFileRequest = {
  file_path: string;
  target_users: string[]; // uid list
  permission?: 'read' | 'write';
};

export type ShareResult = {
  target_user: string;
  success: boolean;
  message: string;
  link?: string;
};

export type ShareFileResponse = {
  results: ShareResult[];
  success_count: number;
  updated_count: number;
  fail_count: number;
  link_code?: string;
};

export type AcceptShareRequest = {
  shareId: string;
};

export type AcceptShareResponse = {
  success: boolean;
  filePath: string;
};

export type ShareInfoRequest = {
  shareId: string;
};

export type ShareInfoResponse = {
  file_path: string;
  sharing_user: string;
  created_at: string;
  status: 'pending' | 'accepted' | 'active' | 'rejected' | 'expired' | 'revoked';
};

export type SharedFolder = {
  id: number;
  file_path: string;
  sharing_user: string;
  target_user: string;
  with_backup: boolean;
  status: 'pending' | 'active' | 'rejected' | 'revoked' | 'expired';
  share_type: 'read' | 'write' | 'full';
  backup_path: string | null;
  symbol_link_path: string | null;
  // expires_at: string | null;
  // accessed_count: number;
  // last_accessed_at: string | null;
  share_link: string;
  created_at: string;
  updated_at: string;
};

export type GetSharedFoldersRequest = {
  page?: number;
  page_size?: number;
};

export type GetSharedFoldersResponse = {
  shares: SharedFolder[];
  total: number;
  page: number;
  page_size: number;
};

export type RevokeShareRequest = {
  shareId: number;
};

export type RevokeShareResponse = {
  success: boolean;
  message: string;
};

export type UpdateSharePermissionRequest = {
  shareId: number;
  share_type: 'read' | 'write' | 'full';
};

export type BatchUpdateSharePermissionRequest = {
  shareIds: number[];
  share_type: 'read' | 'write' | 'full';
};

export type UpdateSharePermissionResponse = {
  success: boolean;
  message: string;
};

export type UpdateResult = {
  share_id: number;
  success: boolean;
  message: string;
};

export type BatchUpdateSharePermissionResponse = {
  results: UpdateResult[];
  success_count: number;
  fail_count: number;
};

/**
 * Get list of users that can be shared with
 */
export const getShareableUsers = async (params: ShareUsersParam): Promise<Response<ShareUsersData> | undefined> => {
  try {
    // TODO: Replace with actual backend endpoint
    const response = await axiosInstance.post<Response<ShareUsersData> | undefined>('/share/users', params, {
      headers: {
        'Content-Type': 'application/json'
      },
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get shareable users', e);
    return undefined
  }
};

/**
 * Share a file or folder with specified users
 * TODO: Backend implementation needed
 */
export const shareFile = async (params: ShareFileRequest): Promise<Response<ShareFileResponse> | undefined> => {
  try {
    // If backup is requested, create backup first (client-side handling for now)
    // TODO: Backend should handle backup creation

    // TODO: Replace with actual backend endpoint
    const response = await axiosInstance.post<Response<ShareFileResponse>>('/share/create', params, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to share file', e);
    // Placeholder: Return mock data for now
    return undefined
  }
};

/**
 * Get information about a shared file
 * TODO: Backend implementation needed
 */
export const getShareInfo = async (params: ShareInfoRequest): Promise<Response<ShareInfoResponse> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<ShareInfoResponse>>(`/share/info/${params.shareId}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get share info', e);
    return undefined;
  }
};

/**
 * Accept a shared file
 * TODO: Backend implementation needed
 */
export const acceptShare = async (params: AcceptShareRequest): Promise<Response<AcceptShareResponse> | undefined> => {
  try {
    // TODO: Replace with actual backend endpoint
    const response = await axiosInstance.post<Response<AcceptShareResponse>>(`/share/accept/${params.shareId}`,{}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to accept share', e);
    return undefined;
  }
};

/**
 * Reject a shared file invitation
 */
export const rejectShare = async (params: AcceptShareRequest): Promise<Response<{ id: number; share_link: string; status: 'rejected' }> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<{ id: number; share_link: string; status: 'rejected' }>>(`/share/reject/${params.shareId}`, {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to reject share', e);
    return undefined;
  }
};

/**
 * Get all shared folders by current user
 * TODO: Backend implementation needed
 */
export const getSharedFolders = async (params?: GetSharedFoldersRequest): Promise<Response<GetSharedFoldersResponse> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<GetSharedFoldersResponse>>('/share/my-shares', {
      params: params,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get shared folders', e);
    return undefined;
  }
};

/**
 * Revoke a share
 * TODO: Backend implementation needed
 */
export const revokeShare = async (params: RevokeShareRequest): Promise<Response<RevokeShareResponse> | undefined> => {
  try {
    const response = await axiosInstance.post<Response<RevokeShareResponse>>(`/share/revoke/${params.shareId}`, {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to revoke share', e);
    return undefined;
  }
};


/**
 * Batch update share permissions
 */
export const batchUpdateSharePermission = async (params: BatchUpdateSharePermissionRequest): Promise<Response<BatchUpdateSharePermissionResponse> | undefined> => {
  try {
    const response = await axiosInstance.put<Response<BatchUpdateSharePermissionResponse>>('/share/permission', {
      share_ids: params.shareIds,
      share_type: params.share_type
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to batch update share permissions', e);
    return undefined;
  }
};

export type SharedWithMeEntry = SharedFolder & {
    file_name: string;
    is_dir: boolean;
    expires_at: string | null;
    accessed_count: number;
    last_accessed_at: string | null;
  };

export type PendingShareEntry = SharedFolder & {
  file_name: string;
  is_dir: boolean;
  accessed_count: number;
};

export const getShareWithMe = async (params?: GetSharedFoldersRequest): Promise<Response<SharedWithMeEntry[]> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<SharedWithMeEntry[]>>('/share/shared-with-me', {
      params: params,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get shared-with-me', e);
    return undefined;
  }
};

export const getShareWithMePending = async (): Promise<Response<PendingShareEntry[]> | undefined> => {
  try {
    const response = await axiosInstance.get<Response<PendingShareEntry[]>>('/share/shared-with-me/pending', {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response?.data;
  } catch (e) {
    console.error('Failed to get pending shared-with-me', e);
    return undefined;
  }
};
